import { ImageUp, Trash2 } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type ChangeEvent, type ReactNode, useRef, useState } from "react";
import toast from "react-hot-toast";
import ConfirmDialog from "./ConfirmDialog.tsx";
import ImageCropDialog from "./ImageCropDialog.tsx";

const MB = 1024 * 1024;

interface ImageUploadFieldProps {
  /** The current image (or its initials stand-in): a `UserAvatar`, a `TenantAvatar`. */
  preview: ReactNode;
  /** An image is stored: the button says Change, and Remove shows. */
  hasImage: boolean;
  /** MIME types the picker offers and the field accepts. */
  accept: string[];
  /** Under the buttons: the accepted types and size, what shows without one. */
  caption?: ReactNode;
  /** Crop to a square before uploading (vector images skip it). */
  crop?: { shape: "circle" | "square"; title?: string };
  maxBytes?: number;
  /** Resolves once stored; the crop dialog stays open (and spins) until then. */
  onUpload: (file: File) => Promise<unknown>;
  onRemove?: () => Promise<unknown>;
  removeConfirm?: { title: string; body?: ReactNode };
}

/** A picture for a person or thing: its preview, upload or change it (cropped), remove it. */
export default function ImageUploadField({
  preview,
  hasImage,
  accept,
  caption,
  crop,
  maxBytes = 2 * MB,
  onUpload,
  onRemove,
  removeConfirm,
}: ImageUploadFieldProps) {
  const input = useRef<HTMLInputElement>(null);
  const [cropping, setCropping] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const upload = async (file: File) => {
    setUploading(true);
    try {
      await onUpload(file);
      setCropping(null);
    } catch {
      // The mutation's error toast says what went wrong.
    } finally {
      setUploading(false);
    }
  };

  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires.
    e.target.value = "";
    if (!file) return;
    if (!accept.includes(file.type)) {
      toast.error("That file type isn't supported");
      return;
    }
    // A crop re-encodes a large photo down to a small one, so only check size without.
    if (crop && file.type !== "image/svg+xml") {
      setCropping(file);
      return;
    }
    if (file.size > maxBytes) {
      toast.error(`The file is over ${Math.round(maxBytes / MB)} MB`);
      return;
    }
    void upload(file);
  };

  const remove = async () => {
    if (!onRemove) return;
    setRemoving(true);
    try {
      await onRemove();
      setConfirmRemove(false);
    } catch {
      // Toasted by the mutation.
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={2.5} sx={{ alignItems: "center" }}>
        {preview}
        <Box>
          <input ref={input} type="file" accept={accept.join(",")} hidden onChange={pick} />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ImageUp />}
              onClick={() => input.current?.click()}
              loading={uploading && !cropping}
            >
              {hasImage ? "Change" : "Upload"}
            </Button>
            {hasImage && onRemove && (
              <Button
                variant="text"
                size="small"
                color="error"
                startIcon={<Trash2 />}
                onClick={() => (removeConfirm ? setConfirmRemove(true) : void remove())}
              >
                Remove
              </Button>
            )}
          </Stack>
          {caption != null && (
            <Typography variant="caption" component="div" sx={{ color: "text.secondary", mt: 1 }}>
              {caption}
            </Typography>
          )}
        </Box>
      </Stack>
      {crop && (
        <ImageCropDialog
          file={cropping}
          shape={crop.shape}
          title={crop.title}
          pending={uploading}
          onCancel={() => setCropping(null)}
          onCrop={(file) => void upload(file)}
        />
      )}
      {removeConfirm && (
        <ConfirmDialog
          open={confirmRemove}
          title={removeConfirm.title}
          confirmLabel="Remove"
          pending={removing}
          onConfirm={() => void remove()}
          onClose={() => setConfirmRemove(false)}
        >
          {removeConfirm.body}
        </ConfirmDialog>
      )}
    </>
  );
}
